// SupabaseBackend: the single shared client + Google player identity used by
// both storage and leaderboard adapters. One per SDK instance.
//
// - Lazily imports @supabase/supabase-js so non-Supabase builds (and Node tests)
//   never need the dependency installed.
// - Real Google sign-in (OAuth redirect) when the game runs at its own URL.
// - Single sign-on when embedded in the Butterfly website iframe: the parent
//   forwards the player's session and the game adopts it (same identity, real
//   name on leaderboards). Only the public anon key + the user's own session
//   tokens are ever used — never a service_role/secret key.

import { isEmbedded } from '../platform/env.js';

// Does `origin` match one of the allowlist patterns? A single '*' in a pattern
// matches one subdomain label (e.g. 'https://*.pages.dev' → 'https://x.pages.dev').
export function isTrustedOrigin(origin, patterns = []) {
  if (!origin || !Array.isArray(patterns)) return false;
  return patterns.some((pattern) => {
    if (!pattern) return false;
    if (pattern === origin) return true;
    if (!pattern.includes('*')) return false;
    const rx = new RegExp('^' + pattern.split('*').map((s) =>
      s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('[^.]+') + '$');
    return rx.test(origin);
  });
}

export class SupabaseBackend {
  constructor(cfg, identityCfg, ctx) {
    this.cfg = cfg;
    this.log = ctx.log;
    this.emit = ctx.emit || (() => {});
    this.client = null;
    this.ready = false;            // true once we have a signed-in user
    this.embedded = isEmbedded();  // running inside the Butterfly website iframe?
    this.trustedParentOrigins = cfg?.auth?.trustedParentOrigins || [];
    this._defaultName = identityCfg?.defaultName || 'Player';
    this.identity = {
      playerId: null,
      displayName: this._defaultName,
      email: null,
      avatarUrl: null,
      isAnonymous: false,          // anonymous auth is no longer used
      signedIn: false,
    };
  }

  async init() {
    if (!this.cfg?.url || !this.cfg?.anonKey) {
      this.log.error('SupabaseBackend: backend.url and backend.anonKey are required.');
      return;
    }
    const mod = await import('@supabase/supabase-js').catch(() => null);
    if (!mod?.createClient) {
      this.log.error('SupabaseBackend: install @supabase/supabase-js in your game.');
      return;
    }
    this.client = mod.createClient(this.cfg.url, this.cfg.anonKey, { auth: this._authOptions() });

    // Keep identity in sync: this also fires right after the OAuth redirect
    // returns, which is how a fresh Google sign-in becomes "ready".
    this.client.auth.onAuthStateChange((_event, session) => {
      this._applySession(session);
    });

    // Restore an existing session (returning player); no auto sign-in.
    const { data: { session } } = await this.client.auth.getSession();
    this._applySession(session);

    // Embedded in the Butterfly website → request the parent's session (SSO).
    if (this.embedded) {
      this._installParentBridge();
    } else if (!this.ready) {
      this.log.info('Supabase ready; waiting for Google sign-in (call signInWithGoogle()).');
    }
  }

  // Supabase auth options. Embedded (iframe SSO): the parent website owns the
  // session lifecycle and re-posts butterfly:auth on TOKEN_REFRESHED, so the
  // iframe must NOT persist, auto-refresh, or parse the URL — it only adopts what
  // the parent pushes. Standalone: normal persisted, self-refreshing client that
  // also completes the OAuth redirect.
  _authOptions() {
    return this.embedded
      ? { autoRefreshToken: false, persistSession: false, detectSessionInUrl: false }
      : { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true };
  }

  // Listen for the parent website's session and request it. Single sign-on:
  // the game adopts the player's session so saves/leaderboards share identity.
  _installParentBridge() {
    if (typeof window === 'undefined' || this._bridgeInstalled) return;
    this._bridgeInstalled = true;

    this._onParentMessage = (event) => {
      if (event?.data?.type !== 'butterfly:auth') return;
      if (!isTrustedOrigin(event.origin, this.trustedParentOrigins)) {
        this.log.warn('[auth] ignored session message from untrusted origin:', event.origin);
        return;
      }
      // event.data.session is the user's own tokens — apply, never log/forward.
      this._applyParentSession(event.data.session);
    };
    window.addEventListener('message', this._onParentMessage);

    // Tell the parent we're ready to receive the session.
    try { window.parent.postMessage({ type: 'butterfly:ready' }, '*'); } catch { /* ignore */ }
    this.log.info('[auth] embedded; requested parent session (SSO).');
  }

  // Adopt (or clear) the session forwarded by the parent website.
  async _applyParentSession(session) {
    if (!this.client) return;
    try {
      if (session && session.access_token && session.refresh_token) {
        // setSession triggers onAuthStateChange → _applySession → player:change.
        await this.client.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        });
        this.log.info('[auth] adopted parent session (SSO).');
      } else {
        // Null session = parent signed out. Use signOut() — never setSession(null),
        // which would trigger a getUser() round-trip.
        await this.client.auth.signOut({ scope: 'local' });
        this.log.info('[auth] parent signed out; cleared local session.');
      }
    } catch (err) {
      this.log.warn('[auth] failed to apply parent session', err);
    }
  }

  // Populate (or clear) identity from a Supabase session and notify listeners.
  _applySession(session) {
    const user = session?.user || null;
    if (user) {
      const m = user.user_metadata || {};
      this.identity = {
        playerId: user.id,
        displayName: m.full_name || m.name || user.email || this._defaultName,
        email: user.email || null,
        avatarUrl: m.avatar_url || m.picture || null,
        isAnonymous: false,
        signedIn: true,
      };
      this.ready = true;
      this.log.info('Google identity ready:', this.identity.playerId);
    } else {
      this.identity = {
        playerId: null,
        displayName: this._defaultName,
        email: null,
        avatarUrl: null,
        isAnonymous: false,
        signedIn: false,
      };
      this.ready = false;
    }
    this.emit('player:change', this.getPlayer());
  }

  getPlayer() {
    return { ...this.identity };
  }

  // Start Google sign-in. This REDIRECTS the page to Google and back; on return,
  // onAuthStateChange populates identity. Call from a user gesture (button).
  // When embedded, the parent website owns sign-in — don't redirect the iframe.
  async signInWithGoogle() {
    if (this.embedded) {
      this.log.warn('[auth] signInWithGoogle ignored while embedded; the parent website handles sign-in.');
      return { ignored: true, reason: 'embedded' };
    }
    if (!this.client) throw new Error('Supabase not ready.');
    return this.client.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: this.cfg?.auth?.redirectTo || undefined },
    });
  }

  async signOut() {
    if (!this.client) throw new Error('Supabase not ready.');
    const r = await this.client.auth.signOut();
    this._applySession(null);
    return r;
  }

  // Optional override of the display name shown on leaderboards (defaults to the
  // Google profile name). Persisted locally so it survives reloads.
  setDisplayName(name) {
    this.identity.displayName = String(name || this._defaultName).slice(0, 32);
    this.emit('player:change', this.getPlayer());
    return this.identity.displayName;
  }
}
